"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";
import type { OfflineMesure } from "@/core/offline/db";
import { computeMesureDerived } from "../calculs";
import { NativeSelect } from "@/components/ui/native-select";
import { useSiteUsedDiameters } from "../hooks/useSiteUsedDiameters";
import { getAvailableDiameters } from "../nomenclatures";
import {
  MATERIAUX,
  TARIFICATIONS,
  FONTE_AGES,
  FONTE_TYPES,
  RESEAU_TYPES,
  RESEAU_SENS,
  hasReseauSens,
  formatReseau,
} from "../types";
import type { Orientation } from "../types";

interface MesureFormProps {
  mesure: OfflineMesure;
  onUpdateFields: (mesureId: string, fields: Partial<OfflineMesure>) => Promise<void>;
  /**
   * Mesure de la campagne précédente (si elle existe) pour afficher en rappel
   * discret sous les champs qui évoluent (min, max).
   * Fourni par pin-detail via usePreviousMesure().
   */
  previousMesure?: OfflineMesure;
  /** Date de la campagne précédente (pour l'affichage du rappel) */
  previousCampagneDate?: Date;
}

/** Parse une saisie décimale utilisateur : virgule acceptée, null si vide/invalide */
function parseDecimal(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseFloat(raw.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** Libellés des codes couleur */
const COLOR_LABELS: Record<string, string> = {
  green: "Bon état (< 20%)",
  orange: "Surveillance (20-29%)",
  red: "Alerte (30-39%)",
  dark: "Critique (≥ 40%)",
};

/** Classes CSS des badges couleur (utilise les tokens du design system) */
const COLOR_BADGE_CLASSES: Record<string, string> = {
  green: "bg-corrosion-green text-white",
  orange: "bg-corrosion-orange text-white",
  red: "bg-corrosion-red text-white",
  dark: "bg-corrosion-dark text-white",
};

/** Snippets rapides pour le champ Notes (extraits des rapports de reference de Renaud) */
const NOTES_SNIPPETS = [
  "Calorifuge Armaflex + bitume",
  "Calorifuge Styrofoam + bitume",
  "Calorifuge PIR (Kooltherm) + pare-vapeur",
  "Calorifuge laine de roche",
  "Corrosion exterieure environnementale",
  "Corrosion par piqures internes",
  "Perte d'epaisseur en tete d'epingle",
  "Condensation visible a l'ouverture",
  "Oxydation couche zinc (electrozingue)",
  "Raccord serti / point faible",
  "Reseau non accessible (tremies maconnees)",
  "Fuites observees",
];

/**
 * MesureForm : Formulaire de saisie des mesures de corrosion.
 * Auto-save par champ avec debounce 300ms.
 * Les calculs se déclenchent automatiquement quand les champs sources changent.
 */
export function MesureForm({ mesure, onUpdateFields, previousMesure, previousCampagneDate }: MesureFormProps) {
  // État local pour tous les champs (auto-save vers Dexie)
  // Réseau structuré : type (select) + complément libre + sens conditionnel (Aller/Retour)
  const [reseauType, setReseauType] = useState(mesure.reseauType ?? "");
  const [reseauComplement, setReseauComplement] = useState(
    mesure.reseauComplement ?? mesure.localisation ?? ""
  );
  const [reseauSens, setReseauSens] = useState(mesure.reseauSens ?? "");
  const [diametreExt, setDiametreExt] = useState(mesure.diametreExt?.toString() ?? "");
  const [showDiametreSuggestions, setShowDiametreSuggestions] = useState(false);
  const [dn, setDn] = useState<number | null>(mesure.dn ?? null);
  const [orientation, setOrientation] = useState<Orientation>(
    (mesure.orientation as Orientation) ?? "H"
  );
  const [materiau, setMateriau] = useState(mesure.materiau ?? "");
  const [fonteAge, setFonteAge] = useState(mesure.fonteAge ?? "");
  const [fonteType, setFonteType] = useState(mesure.fonteType ?? "");
  const [tarification, setTarification] = useState<string[]>(mesure.tarification ?? []);
  const [mesureMax, setMesureMax] = useState(mesure.mesureMax?.toString() ?? "");
  const [mesureMin, setMesureMin] = useState(mesure.mesureMin?.toString() ?? "");
  // Épaisseur d'origine : état AFFICHAGE en string (comme mesureMin/Max).
  // Lier l'input à un number reparsé mangeait les saisies intermédiaires
  // ("4," reparsé en 4 : impossible de taper 4,3 ou d'effacer les décimales).
  const [epaisseurStr, setEpaisseurStr] = useState(
    mesure.epaisseurOrigine?.toString() ?? ""
  );
  const [epaisseurManuelle, setEpaisseurManuelle] = useState(
    mesure.epaisseurManuelle ?? false
  );
  const [dnManuel, setDnManuel] = useState(mesure.dnManuel ?? false);
  const [pourcentageCorrosion, setPourcentageCorrosion] = useState<number | null>(
    mesure.pourcentageCorrosion ?? null
  );
  const [codeCouleur, setCodeCouleur] = useState<string | null>(
    mesure.codeCouleur ?? null
  );
  const [notes, setNotes] = useState(mesure.notes ?? "");
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Debounce timer ref + accumulation des champs en attente
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const pendingFieldsRef = useRef<Partial<OfflineMesure>>({});

  /** Auto-save avec accumulation : les appels rapides fusionnent au lieu de s'écraser */
  const autoSave = useCallback(
    (fields: Partial<OfflineMesure>) => {
      // Fusionner avec les champs déjà en attente
      pendingFieldsRef.current = { ...pendingFieldsRef.current, ...fields };
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdateFields(mesure.id, pendingFieldsRef.current);
        pendingFieldsRef.current = {};
      }, 300);
    },
    [mesure.id, onUpdateFields]
  );

  // Flush les champs en attente avant démontage (navigation)
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        // Sauver immédiatement ce qui est en attente
        if (Object.keys(pendingFieldsRef.current).length > 0) {
          onUpdateFields(mesure.id, pendingFieldsRef.current);
          pendingFieldsRef.current = {};
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Recalcule les valeurs dérivées (DN, %, couleur) et sauve tout en UN seul appel.
   * Appelé par chaque onChange qui affecte les calculs, avec les champs modifiés en paramètre.
   * Résout le bug du debounce partagé : plus de conflit entre onChange et useEffect.
   */
  const recalcAndSave = useCallback(
    (changedFields: Partial<OfflineMesure> & {
      _diametreExt?: string;
      _mesureMin?: string;
      _materiau?: string;
      _epaisseurOrigine?: number | null;
      _epaisseurManuelle?: boolean;
      _dn?: number | null;
      _dnManuel?: boolean;
    }) => {
      // Valeurs actuelles (overridées par les champs modifiés)
      const dExtStr = changedFields._diametreExt ?? diametreExt;
      const mMinStr = changedFields._mesureMin ?? mesureMin;
      const mat = changedFields._materiau ?? materiau;
      const manualEp = changedFields._epaisseurOrigine !== undefined
        ? changedFields._epaisseurOrigine
        : parseDecimal(epaisseurStr);
      const currentEpManuelle = changedFields._epaisseurManuelle ?? epaisseurManuelle;
      const currentDnManuel = changedFields._dnManuel ?? dnManuel;
      const currentDn = changedFields._dn !== undefined ? changedFields._dn : dn;

      // Lire les valeurs contextuelles (overridées par changedFields si modifiées dans ce cycle)
      const currentTarif = (changedFields.tarification as string[] | undefined) ?? tarification;
      const currentFonteAge = (changedFields.fonteAge as string | undefined) ?? fonteAge;
      const currentFonteType = (changedFields.fonteType as string | undefined) ?? fonteType;

      // Dérivation métier (DN → épaisseur → % → couleur) déléguée à la fonction pure (I10).
      const derived = computeMesureDerived({
        diametreExt: dExtStr ? parseFloat(dExtStr) : null,
        mesureMin: mMinStr ? parseFloat(mMinStr) : null,
        materiau: mat,
        manualEpaisseur: manualEp,
        epaisseurManuelle: currentEpManuelle,
        manualDn: currentDn,
        dnManuel: currentDnManuel,
        tarification: currentTarif,
        fonteAge: currentFonteAge || undefined,
        fonteType: currentFonteType || undefined,
      });

      setDn(derived.dn);
      // Épaisseur : la saisie manuelle prime (arbitrage Renaud juillet 2026, ex-I13).
      // En mode abaque, on reflète la valeur calculée Y COMPRIS null (pas de
      // valeur fantôme conservée quand l'abaque ne trouve rien, cf. fonte).
      if (!currentEpManuelle) {
        setEpaisseurStr(derived.effectiveEpaisseur?.toString() ?? "");
      }
      setPourcentageCorrosion(derived.pourcentageCorrosion);
      setCodeCouleur(derived.codeCouleur);

      // Construire les champs à sauver (champ modifié + valeurs calculées).
      // Les valeurs dérivées sont sauvées en null explicite quand elles
      // disparaissent (sinon l'ancienne valeur reste en base et au PDF).
      // Retirer les clés internes _xxx
      const {
        _diametreExt, _mesureMin, _materiau, _epaisseurOrigine,
        _epaisseurManuelle, _dn, _dnManuel,
        ...userFields
      } = changedFields;
      const saveFields: Partial<OfflineMesure> = {
        ...userFields,
        dn: derived.dn,
        pourcentageCorrosion: derived.pourcentageCorrosion,
        codeCouleur: derived.codeCouleur,
      };
      if (!currentEpManuelle) {
        saveFields.epaisseurOrigine = derived.effectiveEpaisseur;
      }

      autoSave(saveFields);
    },
    [diametreExt, mesureMin, materiau, epaisseurStr, epaisseurManuelle, dn, dnManuel, tarification, fonteAge, fonteType, autoSave]
  );

  /**
   * Sauvegarde les 3 champs réseau + le champ `localisation` recalculé.
   * Garde localisation synchronisé pour les anciens affichages qui le lisent encore.
   */
  const saveReseau = useCallback(
    (next: { reseauType?: string; reseauComplement?: string; reseauSens?: string }) => {
      const type = next.reseauType !== undefined ? next.reseauType : reseauType;
      const complement = next.reseauComplement !== undefined ? next.reseauComplement : reseauComplement;
      const sens = next.reseauSens !== undefined ? next.reseauSens : reseauSens;
      const effectiveSens = hasReseauSens(type) ? sens : "";
      const display = formatReseau({
        reseauType: type || null,
        reseauComplement: complement || null,
        reseauSens: effectiveSens || null,
      });
      autoSave({
        reseauType: type || null,
        reseauComplement: complement || null,
        reseauSens: effectiveSens || null,
        localisation: display || null,
      });
    },
    [reseauType, reseauComplement, reseauSens, autoSave]
  );

  const showSens = hasReseauSens(reseauType);

  // % corrosion négatif = donnée impossible (épaisseur min mesurée > origine).
  const corrosionIncoherente =
    pourcentageCorrosion !== null && pourcentageCorrosion < 0;

  /** Insere un snippet dans le champ Notes a la position du curseur (ou en fin de texte) */
  function insertSnippet(snippet: string) {
    const el = notesRef.current;
    if (el) {
      const start = el.selectionStart ?? notes.length;
      const end = el.selectionEnd ?? notes.length;
      const before = notes.slice(0, start);
      const after = notes.slice(end);
      const sep = before && !before.endsWith("\n") ? "\n" : "";
      const newVal = before + sep + snippet + (after ? "\n" + after : "");
      setNotes(newVal);
      autoSave({ notes: newVal });
      requestAnimationFrame(() => {
        el.focus();
        const pos = before.length + sep.length + snippet.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      const sep = notes && !notes.endsWith("\n") ? "\n" : "";
      const newVal = notes + sep + snippet;
      setNotes(newVal);
      autoSave({ notes: newVal });
    }
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Réseau structuré : type (select) + complément libre + sens conditionnel */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
          Réseau
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="reseauType">Type</Label>
            <NativeSelect
              id="reseauType"
              value={reseauType}
              options={RESEAU_TYPES as unknown as string[]}
              placeholder="Sélectionner un type"
              onChange={(val) => {
                setReseauType(val);
                // Si le nouveau type ne porte pas de sens, on efface le sens précédent
                if (!hasReseauSens(val) && reseauSens) {
                  setReseauSens("");
                  saveReseau({ reseauType: val, reseauSens: "" });
                } else {
                  saveReseau({ reseauType: val });
                }
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reseauComplement">Complément</Label>
            <Input
              id="reseauComplement"
              value={reseauComplement}
              placeholder="Ex : 1 sous-sol"
              onChange={(e) => {
                const val = e.target.value;
                setReseauComplement(val);
                saveReseau({ reseauComplement: val });
              }}
            />
          </div>
        </div>
        {showSens && (
          <div className="space-y-2">
            <Label>Sens</Label>
            <div className="inline-flex h-12 rounded-lg border border-input p-0.5">
              {(RESEAU_SENS as unknown as string[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`rounded-sm px-4 text-sm font-medium transition-colors ${
                    reseauSens === s
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    const next = reseauSens === s ? "" : s;
                    setReseauSens(next);
                    saveReseau({ reseauSens: next });
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Section Tuyau */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
          Tuyau
        </h3>

        <div className="grid grid-cols-[auto_1fr_1fr] items-end gap-3">
          <div className="space-y-2">
            <Label>Orientation</Label>
            <div className="inline-flex h-12 rounded-lg border border-input p-0.5">
              <button
                type="button"
                className={`rounded-sm px-4 text-sm font-medium transition-colors ${
                  orientation === "H"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => {
                  setOrientation("H");
                  autoSave({ orientation: "H" });
                }}
              >
                H
              </button>
              <button
                type="button"
                className={`rounded-sm px-4 text-sm font-medium transition-colors ${
                  orientation === "V"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => {
                  setOrientation("V");
                  autoSave({ orientation: "V" });
                }}
              >
                V
              </button>
            </div>
          </div>

          <div className="relative space-y-2">
            <Label htmlFor="diametreExt">∅ ext (mm)</Label>
            <Input
              id="diametreExt"
              type="number"
              inputMode="decimal"
              value={diametreExt}
              placeholder="Ex : 90"
              onChange={(e) => {
                setDiametreExt(e.target.value);
                setShowDiametreSuggestions(!!materiau);
                recalcAndSave({
                  diametreExt: e.target.value ? parseFloat(e.target.value) : undefined,
                  _diametreExt: e.target.value,
                });
              }}
              onFocus={() => setShowDiametreSuggestions(!!materiau)}
              onBlur={() => setTimeout(() => setShowDiametreSuggestions(false), 150)}
            />
            {showDiametreSuggestions && materiau && (
              <DiametreSuggestions
                materiau={materiau}
                tarification={tarification}
                filter={diametreExt}
                pinId={mesure.pinId}
                onSelect={(val) => {
                  const str = String(val);
                  setDiametreExt(str);
                  setShowDiametreSuggestions(false);
                  recalcAndSave({
                    diametreExt: val,
                    _diametreExt: str,
                  });
                }}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dn">{materiau === "Fonte" ? "DN" : "DN (auto)"}</Label>
            {materiau === "Fonte" ? (
              // Fonte : la désignation terrain est le DN, saisissable directement
              // (le ∅ ext devient optionnel). Un DN saisi prime sur le DN déduit.
              <Input
                id="dn"
                type="number"
                inputMode="numeric"
                value={dn ?? ""}
                placeholder="Ex : 100"
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : null;
                  const isManual = val !== null;
                  setDn(val);
                  setDnManuel(isManual);
                  recalcAndSave({
                    dnManuel: isManual,
                    _dn: val,
                    _dnManuel: isManual,
                  });
                }}
              />
            ) : (
              <Input
                id="dn"
                value={dn !== null ? dn : "-"}
                disabled
                className="bg-muted"
              />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="materiau">Matériau</Label>
          <NativeSelect
            id="materiau"
            value={materiau}
            options={MATERIAUX as unknown as string[]}
            placeholder="Sélectionner un matériau"
            onChange={(val) => {
              setMateriau(val);
              // Reset champs conditionnels si on change de matériau
              const resetFonte = val !== "Fonte";
              const resetTarif = val !== "Acier noir" && val !== "Acier galvanisé";
              const updates: Record<string, unknown> = { materiau: val, _materiau: val };
              if (resetFonte) {
                setFonteAge("");
                setFonteType("");
                updates.fonteAge = undefined;
                updates.fonteType = undefined;
              }
              if (resetTarif) {
                setTarification([]);
                updates.tarification = [];
              }
              // Changer de matériau invalide les saisies manuelles (DN fonte,
              // épaisseur) : retour au mode abaque du nouveau matériau.
              setDnManuel(false);
              setEpaisseurManuelle(false);
              updates.dnManuel = false;
              updates.epaisseurManuelle = false;
              updates._dnManuel = false;
              updates._epaisseurManuelle = false;
              recalcAndSave(updates);
            }}
          />
        </div>

        {materiau === "Fonte" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Âge</Label>
              <div className="inline-flex rounded-lg border border-input p-0.5">
                {FONTE_AGES.map((age) => (
                  <button
                    key={age}
                    type="button"
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                      fonteAge === age
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => {
                      setFonteAge(age);
                      recalcAndSave({ fonteAge: age });
                    }}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <div className="inline-flex rounded-lg border border-input p-0.5">
                {FONTE_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                      fonteType === type
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => {
                      setFonteType(type);
                      recalcAndSave({ fonteType: type });
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="mesureMax">Épaisseur max (mm)</Label>
            <Input
              id="mesureMax"
              type="number"
              inputMode="decimal"
              value={mesureMax}
              placeholder="Ex : 8.5"
              onChange={(e) => {
                setMesureMax(e.target.value);
                autoSave({
                  mesureMax: e.target.value ? parseFloat(e.target.value) : undefined,
                });
              }}
            />
            {previousMesure?.mesureMax != null && previousCampagneDate && (
              <p className="text-2xs italic text-muted-foreground flex items-center gap-1">
                <span aria-hidden>↳</span>
                <span>
                  Précédente : <span className="font-medium">{previousMesure.mesureMax} mm</span>
                  <span className="opacity-70">, {previousCampagneDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
                </span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mesureMin">Épaisseur min (mm)</Label>
            <Input
              id="mesureMin"
              type="number"
              inputMode="decimal"
              value={mesureMin}
              placeholder="Ex : 6.2"
              onChange={(e) => {
                setMesureMin(e.target.value);
                recalcAndSave({
                  mesureMin: e.target.value ? parseFloat(e.target.value) : undefined,
                  _mesureMin: e.target.value,
                });
              }}
            />
            {previousMesure?.mesureMin != null && previousCampagneDate && (
              <p className="text-2xs italic text-muted-foreground flex items-center gap-1">
                <span aria-hidden>↳</span>
                <span>
                  Précédente : <span className="font-medium">{previousMesure.mesureMin} mm</span>
                  <span className="opacity-70">, {previousCampagneDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
                </span>
              </p>
            )}
          </div>
        </div>

        {(materiau === "Acier noir" || materiau === "Acier galvanisé") && (
        <div className="space-y-2">
          <Label>Tarification</Label>
          <div className="flex flex-wrap gap-2">
            {(TARIFICATIONS as unknown as string[]).map((t) => {
              const checked = tarification.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    checked
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => {
                    const next = checked
                      ? tarification.filter((v) => v !== t)
                      : [...tarification, t];
                    setTarification(next);
                    recalcAndSave({ tarification: next });
                  }}
                >
                  {checked ? "✓ " : ""}{t}
                </button>
              );
            })}
          </div>
        </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="epaisseurOrigine">Épaisseur d&apos;origine (mm)</Label>
          <Input
            id="epaisseurOrigine"
            type="text"
            inputMode="decimal"
            value={epaisseurStr}
            placeholder="Abaque automatique ou saisir manuellement"
            onChange={(e) => {
              // type="text" + état string : la virgule et les états
              // intermédiaires ("4,") restent affichés pendant la frappe.
              // Seuls chiffres + un séparateur décimal sont acceptés (pas de
              // "abc" ni "4,3x" qui activeraient le mode manuel à tort).
              const raw = e.target.value;
              if (!/^\d*[.,]?\d*$/.test(raw)) return;
              const val = parseDecimal(raw);
              // Saisir une valeur passe en mode manuel (prioritaire sur l'abaque).
              // Vider le champ revient au mode abaque.
              const isManual = /\d/.test(raw);
              setEpaisseurStr(raw);
              setEpaisseurManuelle(isManual);
              recalcAndSave({
                epaisseurOrigine: val,
                epaisseurManuelle: isManual,
                _epaisseurOrigine: val,
                _epaisseurManuelle: isManual,
              });
            }}
          />
          {epaisseurManuelle ? (
            <p className="text-2xs text-muted-foreground flex items-center gap-2">
              <span>Valeur saisie manuellement (prioritaire sur l&apos;abaque).</span>
              <button
                type="button"
                className="text-primary underline shrink-0"
                onClick={() => {
                  setEpaisseurManuelle(false);
                  recalcAndSave({
                    epaisseurManuelle: false,
                    _epaisseurManuelle: false,
                  });
                }}
              >
                Revenir à l&apos;abaque
              </button>
            </p>
          ) : materiau === "Fonte" && (!fonteAge || !fonteType) ? (
            <p className="text-2xs text-muted-foreground">
              Choisir l&apos;âge et le type pour l&apos;épaisseur abaque, ou saisir la valeur.
            </p>
          ) : materiau === "Fonte" && !epaisseurStr ? (
            <p className="text-2xs text-muted-foreground">
              Hors abaque pour ce DN : saisir l&apos;épaisseur.
            </p>
          ) : null}
        </div>
      </section>

      {/* Section Résultats */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
          Résultats
        </h3>

        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">% corrosion</span>
            <span className={`font-medium ${corrosionIncoherente ? "text-destructive" : ""}`}>
              {pourcentageCorrosion !== null ? `${pourcentageCorrosion}%` : "-"}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">État</span>
            {codeCouleur ? (
              <Badge className={COLOR_BADGE_CLASSES[codeCouleur] ?? ""}>
                {COLOR_LABELS[codeCouleur] ?? codeCouleur}
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">-</span>
            )}
          </div>

          {/* Donnée incohérente : épaisseur min mesurée > épaisseur d'origine
              (souvent après un changement de matériau). On alerte clairement
              Renaud au lieu de masquer (pas de pastille = trop discret). */}
          {corrosionIncoherente && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-2.5 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              <span>
                Données incohérentes : l'épaisseur minimale mesurée dépasse
                l'épaisseur d'origine. Vérifiez le matériau et les épaisseurs
                min/max saisies.
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Section Notes */}
      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-primary tracking-wide border-b border-border pb-1">
          Notes
        </h3>

        {/* Snippets rapides */}
        <div className="flex flex-wrap gap-1.5">
          {NOTES_SNIPPETS.map((snippet) => (
            <button
              key={snippet}
              type="button"
              onClick={() => insertSnippet(snippet)}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted hover:bg-accent hover:text-accent-foreground border border-border transition-colors"
            >
              {snippet}
            </button>
          ))}
        </div>

        <Textarea
          ref={notesRef}
          value={notes}
          placeholder="Observations, remarques..."
          rows={10}
          onChange={(e) => {
            setNotes(e.target.value);
            autoSave({ notes: e.target.value });
          }}
        />
      </section>

    </div>
  );
}

/** Liste de suggestions ∅ ext : valeurs déjà utilisées sur le site + valeurs normées ABAC */
function DiametreSuggestions({
  materiau,
  tarification,
  filter,
  pinId,
  onSelect,
}: {
  materiau: string;
  tarification: string[];
  filter: string;
  pinId: string;
  onSelect: (value: number) => void;
}) {
  // Diamètres déjà utilisés sur le site (accès Dexie extrait dans un hook : I10)
  const usedDiameters = useSiteUsedDiameters(pinId);

  // Diamètres normés ABAC pour ce matériau
  const abacDiameters = getAvailableDiameters(materiau, tarification);

  // Filtrer par la saisie en cours
  const filterNum = filter ? parseFloat(filter) : null;
  const filterUsed = filterNum
    ? usedDiameters.filter((d) => String(d).includes(filter))
    : usedDiameters;
  const filterAbac = filterNum
    ? abacDiameters.filter((d) => String(d).includes(filter))
    : abacDiameters;

  // Retirer des ABAC les valeurs déjà dans "utilisées" (éviter les doublons)
  const usedSet = new Set(filterUsed);
  const abacOnly = filterAbac.filter((d) => !usedSet.has(d));

  if (filterUsed.length === 0 && abacOnly.length === 0) return null;

  return (
    <ul className="absolute top-full left-0 right-0 z-20 mt-1 max-h-48 overflow-y-auto rounded-md border border-input bg-background shadow-md">
      {filterUsed.length > 0 && (
        <>
          <li className="px-3 py-1 text-xs font-semibold text-muted-foreground bg-muted/50">
            Déjà utilisés
          </li>
          {filterUsed.map((d) => (
            <li key={`used-${d}`}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(d)}
              >
                {d} mm
              </button>
            </li>
          ))}
        </>
      )}
      {abacOnly.length > 0 && (
        <>
          <li className="px-3 py-1 text-xs font-semibold text-muted-foreground bg-muted/50">
            Valeurs normées
          </li>
          {abacOnly.map((d) => (
            <li key={`abac-${d}`}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(d)}
              >
                {d} mm
              </button>
            </li>
          ))}
        </>
      )}
    </ul>
  );
}

